import {useEffect, useState} from 'react'
import Modal from '../Modal'
import InputField from '../InputField'
import Select from '../Select'
import DatePicker from "../DatePicker";
import {isDateValid} from '../../utils/dateHelper'
import {Colors} from '../../constants/colors'
import {createUseStyles} from 'react-jss'
import Switch from '../Switch'
import {
  CreateOrUpdateInAppMessageModel,
  InAppMessage,
  InAppMessageSeverity,
  InAppMessageType
} from "../../redux/inAppMessageTypes";

interface Props {
  initialValue?: InAppMessage | undefined
  onAddClick?: (model: CreateOrUpdateInAppMessageModel) => void
  onCancelClick?: () => void
  visible: boolean
}

const CreateOrUpdateInAppMessageModal = ({initialValue, onAddClick, onCancelClick, visible}: Props) => {
  const styles = useStyles()

  const defaultValidState = {
    internalNameValid: true,
    iconValid: true,
    minAppVersionValid: true,
    fromDateValid: true,
    toDateValid: true
  }

  const emptyMessage = () => {
    return {
      employerId: undefined,
      internalName: '',
      icon: '',
      url: undefined,
      validFrom: new Date().toISOString(),
      validTo: undefined,
      sticky: false,
      minAppVersion: '1.0.0',
      priority: 1,
      type: InAppMessageType.Modal,
      severity: InAppMessageSeverity.Info
    }
  }

  const [message, setMessage] = useState<CreateOrUpdateInAppMessageModel>(emptyMessage)
  const [valid, setValid] = useState<{
    internalNameValid: boolean
    iconValid: boolean
    minAppVersionValid: boolean
    fromDateValid: boolean
    toDateValid: boolean
  }>(defaultValidState)

  useEffect(() => {
    if (initialValue) {
      setMessage({
        internalName: initialValue.internalName,
        icon: initialValue.icon,
        url: initialValue.url,
        validFrom: initialValue.validFrom,
        validTo: initialValue.validTo,
        sticky: initialValue.sticky,
        minAppVersion: initialValue.minAppVersion,
        priority: initialValue.priority,
        type: initialValue.type,
        severity: initialValue.severity
      })
    } else {
      setMessage(emptyMessage)
    }
  }, [initialValue])

  const validateInput = () => {
    const appVersionRegex = /^(\d+\.)(\d+\.)(\d+)$/

    let validClone = {...valid}

    validClone.internalNameValid = message.internalName.length > 0
    validClone.iconValid = message.icon.length > 0
    validClone.minAppVersionValid = appVersionRegex.test(message.minAppVersion)
    validClone.fromDateValid = isDateValid(message.validFrom)
    validClone.toDateValid = message.validTo == null || message.validTo === '' || isDateValid(message.validTo)

    setValid(validClone)

    return (
      validClone.internalNameValid &&
      validClone.iconValid &&
      validClone.minAppVersionValid &&
      validClone.fromDateValid &&
      validClone.toDateValid)
  }

  const handleOnAdd = () => {
    if (!validateInput()) {
      return
    }

    onAddClick && onAddClick(message)
    setValid(defaultValidState)

    if (!initialValue) {
      setMessage(emptyMessage)
    }
  }

  const handleOnCancel = () => {
    onCancelClick && onCancelClick()

    if (!initialValue) {
      setMessage(emptyMessage)
    }
  }

  const handleOnChange = (propName: string, value: any) => {
    setMessage({...message, [propName]: value})
  }

  return (
    <Modal
      title={initialValue ? 'Edit in-app message' : 'Create in-app message'}
      show={visible}
      okButtonTitle='Save'
      cancelButtonTitle='Cancel'
      scrollable={false}
      onHide={() => handleOnCancel()}
      onOkClick={() => handleOnAdd()}
      onCancelClick={() => handleOnCancel()}
    >
      <InputField
        id='internalName'
        label='Internal name'
        value={message.internalName}
        onChange={(event) => handleOnChange('internalName', event.target.value)}
        placeholder='My in-app message'
        valid={valid.internalNameValid}
      />
      <DatePicker
        id='validFrom'
        label='Valid from'
        includeTime={true}
        value={message.validFrom}
        onChange={value => handleOnChange('validFrom', value)}
        valid={valid.fromDateValid}
      />
      <DatePicker
        id='validTo'
        label='Valid to'
        includeTime={true}
        value={message.validTo}
        onChange={value => handleOnChange('validTo', value)}
        valid={valid.toDateValid}
      />
      <Switch
        id='sticky'
        key='sticky'
        checked={message?.sticky ?? false}
        label='Sticky'
        onChange={() => handleOnChange('sticky', !(message?.sticky ?? false))}
      />
      <InputField
        id='minAppVersion'
        label='Min app version'
        value={message.minAppVersion}
        onChange={(event) => handleOnChange('minAppVersion', event.target.value)}
        placeholder='X.Y.Z'
        valid={valid.minAppVersionValid}
      />
      <InputField
        id='icon'
        label='Icon'
        value={message.icon}
        onChange={(event) => handleOnChange('icon', event.target.value)}
        placeholder='calendar'
        valid={valid.iconValid}
      />
      <InputField
        id='url'
        label='Url'
        value={message.url ?? ''}
        onChange={(event) => handleOnChange('url', event.target.value)}
        placeholder='Ex. cappy://learn'
      />
      <InputField
        numeric
        id='priority'
        label='Priority'
        value={message.priority.toString()}
        onChange={(event) => handleOnChange('priority', event.target.value)}
      />
      <Select
        id='typeSelector'
        label='Type'
        options={[
          {name: 'Modal', value: InAppMessageType.Modal},
          {name: 'Login Screen Banner', value: InAppMessageType.LoginScreenBanner},
          {name: 'Payout System Status', value: InAppMessageType.PayoutSystemStatus, disabled: true},
        ]}
        selectedValue={message.type}
        onSelect={(value) => handleOnChange('placement', value)}
      />
      <Select
        id='severitySelector'
        label='Severity'
        options={[
          {name: 'Info', value: InAppMessageSeverity.Info},
          {name: 'Warning', value: InAppMessageSeverity.Warning},
          {name: 'Error', value: InAppMessageSeverity.Error},
          {name: 'Critical', value: InAppMessageSeverity.Critical}
        ]}
        selectedValue={message.severity}
        onSelect={(value) => handleOnChange('severity', value)}
      />
      <a className={styles.externalLink} target='blank' href={`https://feathericons.com`}>https://feathericons.com</a>
    </Modal>
  )
}

export default CreateOrUpdateInAppMessageModal

const useStyles = createUseStyles({
  externalLink: {
    marginLeft: 5,
    cursor: 'pointer',
    '&:hover': {
      color: Colors.euro_600,
    },
  }
})
